import { format } from "date-fns";
import { vi } from "date-fns/locale";

export interface FlattenedMaterialItem {
  id: string;
  materialPlanId: string;
  materialCode: string;
  materialName: string;
  materialCategory?: string | null;
  typeLabel?: string | null;
  suppliedSize?: string | null;
  unit: string;
  qtyRequired: number;
  qtyAvailable: number;
  shortageQty: number;
  status: string;
  expectedDate?: string | null;
  vendorName?: string | null;
  poId: string;
  poCode: string;
  productName: string;
  customerName: string;
  deadline?: string | null;
  poStatus: string;
  materialStatus: string;
}

export interface UnitBreakdown {
  unit: string;
  qtyRequired: number;
  qtyAvailable: number;
  shortageQty: number;
}

export interface MaterialDailySubtotal {
  totalRequired: number;
  totalAvailable: number;
  totalShortage: number;
  totalItems: number;
  totalPOs: number;
  fulfillmentPercent: number;
  byUnit: Record<string, UnitBreakdown>;
  isMixedUnits: boolean;
  primaryUnit: string;
}

export interface MaterialDateGroup {
  dateKey: string; // YYYY-MM-DD or "NO_DATE"
  displayDate: string; // "Thứ Năm, 08/10/2026" or "Chờ xếp lịch"
  rawDate: Date | null;
  items: FlattenedMaterialItem[];
  subtotal: MaterialDailySubtotal;
}

/**
 * Tính toán Subtotal tổng kết nhu cầu vật tư trong ngày (tuân thủ Rule 11 & phân tách ĐVT)
 */
export function calculateMaterialDailySubtotal(items: FlattenedMaterialItem[]): MaterialDailySubtotal {
  let totalRequired = 0;
  let totalAvailable = 0;
  let totalShortage = 0;
  const poIds = new Set<string>();
  const byUnit: Record<string, UnitBreakdown> = {};

  for (const item of items) {
    const req = Number(item.qtyRequired) || 0;
    const avail = Number(item.qtyAvailable) || 0;
    const short = Number(item.shortageQty) || 0;
    const unitKey = (item.unit || "tờ").trim().toLowerCase();

    totalRequired += req;
    totalAvailable += avail;
    totalShortage += short;
    if (item.poId) poIds.add(item.poId);

    if (!byUnit[unitKey]) {
      byUnit[unitKey] = {
        unit: item.unit || "tờ",
        qtyRequired: 0,
        qtyAvailable: 0,
        shortageQty: 0,
      };
    }
    byUnit[unitKey].qtyRequired += req;
    byUnit[unitKey].qtyAvailable += avail;
    byUnit[unitKey].shortageQty += short;
  }

  const unitKeys = Object.keys(byUnit);
  const isMixedUnits = unitKeys.length > 1;

  // Xác định đơn vị tính chủ đạo (chiếm số lượng lớn nhất)
  let primaryUnit = unitKeys.length > 0 ? byUnit[unitKeys[0]].unit : "tờ";
  let maxReq = -1;
  for (const k of unitKeys) {
    if (byUnit[k].qtyRequired > maxReq) {
      maxReq = byUnit[k].qtyRequired;
      primaryUnit = byUnit[k].unit;
    }
  }

  const fulfillmentPercent =
    totalRequired > 0
      ? Math.min(100, Math.round(((totalRequired - totalShortage) / totalRequired) * 100))
      : 100;

  return {
    totalRequired,
    totalAvailable,
    totalShortage,
    totalItems: items.length,
    totalPOs: poIds.size,
    fulfillmentPercent,
    byUnit,
    isMixedUnits,
    primaryUnit,
  };
}

/**
 * Gom nhóm toàn bộ vật tư từ các Lệnh sản xuất theo ngày cần vật tư (expectedDate hoặc deadline)
 */
export function groupMaterialsByExpectedDate(pos: any[] = []): MaterialDateGroup[] {
  const groupsMap = new Map<string, FlattenedMaterialItem[]>();
  const noDateItems: FlattenedMaterialItem[] = [];

  for (const po of pos) {
    const materials = po.materials || [];
    for (const mat of materials) {
      const flattened: FlattenedMaterialItem = {
        id: mat.id || mat.materialPlanId,
        materialPlanId: mat.materialPlanId || mat.id,
        materialCode: mat.materialCode || "",
        materialName: mat.materialName || "",
        materialCategory: mat.materialCategory,
        typeLabel: mat.typeLabel,
        suppliedSize: mat.suppliedSize,
        unit: mat.unit || "tờ",
        qtyRequired: Number(mat.qtyRequired || mat.neededQty) || 0,
        qtyAvailable: Number(mat.qtyAvailable || mat.stockQty) || 0,
        shortageQty: Number(mat.shortageQty) || 0,
        status: mat.status || "PENDING",
        expectedDate: mat.expectedDate || po.deadline,
        vendorName: mat.vendorName,
        poId: po.id,
        poCode: po.orderId,
        productName: po.productName,
        customerName: po.customerName,
        deadline: po.deadline,
        poStatus: po.status,
        materialStatus: po.materialStatus,
      };

      const dateStr = mat.expectedDate || po.deadline;
      if (!dateStr) {
        noDateItems.push(flattened);
        continue;
      }

      try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) {
          noDateItems.push(flattened);
          continue;
        }
        const key = format(d, "yyyy-MM-dd");
        if (!groupsMap.has(key)) {
          groupsMap.set(key, []);
        }
        groupsMap.get(key)!.push(flattened);
      } catch {
        noDateItems.push(flattened);
      }
    }
  }

  // Sắp xếp các ngày giảm dần (desc)
  const sortedDates = Array.from(groupsMap.keys()).sort((a, b) => b.localeCompare(a));

  const result: MaterialDateGroup[] = sortedDates.map((dateKey) => {
    const groupItems = groupsMap.get(dateKey)!;
    const firstDate = new Date(groupItems[0].expectedDate || dateKey);
    let displayDate = dateKey;
    try {
      const dayName = format(firstDate, "EEEE", { locale: vi });
      const dayFormatted = dayName.charAt(0).toUpperCase() + dayName.slice(1);
      displayDate = `${dayFormatted}, ${format(firstDate, "dd/MM/yyyy")}`;
    } catch {
      displayDate = dateKey;
    }

    return {
      dateKey,
      displayDate,
      rawDate: firstDate,
      items: groupItems,
      subtotal: calculateMaterialDailySubtotal(groupItems),
    };
  });

  // Nếu có vật tư chưa có ngày cần
  if (noDateItems.length > 0) {
    result.push({
      dateKey: "NO_DATE",
      displayDate: "Chưa Có Hạn Cần Vật Tư (Chờ Lập Lịch)",
      rawDate: null,
      items: noDateItems,
      subtotal: calculateMaterialDailySubtotal(noDateItems),
    });
  }

  return result;
}
